// StatefulImage.jsx
'use strict';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

class StatefulImage extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      loadState: props.defaultLoadState || 'loaded', // initialize as 'loaded' for SSR
      errorIndex: 0,
    };
  }
  targetRef = React.createRef();

  onLoad = ({ event, targetOnLoad }) => {
    this.setState({ loadState: 'loaded' });
    if (targetOnLoad) {
      targetOnLoad(event);
    }
  };

  componentDidMount() {
    const imgElement = this.targetRef.current.querySelector('img');
    if (imgElement?.complete) {
      // image loaded (from cache)
      if (imgElement.naturalWidth !== 0) {
        // keep loadState as loading, and set loaded in next event loop for transition
        requestAnimationFrame(() => {
          this.setState({ loadState: 'loaded' });
        });
      } else {
        this.setState({ loadState: 'error' });
      }
    } else {
      this.setState({ loadState: 'loading' });
    }
  }

  render() {
    const { loadState, errorIndex } = this.state;
    const { children } = this.props;
    const [target, loading, errors = []] =
      children instanceof Array
        ? ['target', 'loading', 'error'].map(key => {
            if (key === 'error') {
              return children.filter(
                child => key === child.props?.['data-key']
              );
            }
            return children.find(child => key === child.props?.['data-key']);
          })
        : [children];
    return (
      <StyledStatefulImage>
        <FadeInBlock ref={this.targetRef} loaded={loadState === 'loaded'}>
          {React.isValidElement(target) &&
            React.cloneElement(target, {
              onLoad: event =>
                this.onLoad({ targetOnLoad: target.props['onLoad'], event }),
              onError: () => this.setState({ loadState: 'error' }),
              style: {
                ...target.props['style'],
              },
            })}
        </FadeInBlock>
        {'loading' === loadState && loading && (
          <OverlayBlock>{loading}</OverlayBlock>
        )}
        {'error' === loadState && errors[errorIndex] && (
          <OverlayBlock>
            {React.cloneElement(errors[errorIndex], {
              onError: () => {
                this.setState({
                  errorIndex: errorIndex + 1,
                });
              },
            })}
          </OverlayBlock>
        )}
      </StyledStatefulImage>
    );
  }
}

StatefulImage.propTypes = {
  defaultLoadState: PropTypes.string,
  children: PropTypes.node.isRequired,
};

const StyledStatefulImage = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const OverlayBlock = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const FadeInBlock = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: ${({ loaded }) => (loaded ? 1 : 0)};
  transition: opacity 0.3s ease;
`;

export default StatefulImage;
